import React from 'react';
import { Check, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import type { LayoutSaveState } from '../../hooks/useDashboardLayout';
import { ALL_KPIS, DashboardLayout, KPI_LABEL, KpiId, PRESETS, PresetId, WidgetId, Zone, matchingPreset } from './dashboardLayout';

/**
 * Gemeinsame Teile des Dashboard-Editors — die Leiste am PC und das Blatt am
 * Handy zeigen dieselben drei Dinge: die Vorlagen als Karten, die Auswahl
 * der Kacheln und den Speicherstatus.
 */

/** Skizze der Zonen einer Vorlage: Balken oben/unten, zwei Spalten dazwischen — nur aus divs. */
const PresetSchematic: React.FC<{ zones: Record<Zone, WidgetId[]>; active: boolean }> = ({ zones, active }) => {
    const main = active ? 'bg-blue-500' : 'bg-slate-400';
    const rest = active ? 'bg-blue-300' : 'bg-slate-300';
    return (
        <div aria-hidden="true" className={`w-14 h-11 rounded-md border p-1 flex flex-col gap-[3px] shrink-0 ${active ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}>
            {zones.top.map(id => <div key={id} className={`h-[3px] rounded-[1px] shrink-0 ${rest}`} />)}
            <div className="flex-1 flex gap-[3px] min-h-0">
                <div className="flex-[3] flex flex-col gap-[3px] min-h-0">
                    {zones.main.map(id => <div key={id} className={`flex-1 rounded-[1px] ${main}`} />)}
                </div>
                <div className="flex-[2] flex flex-col gap-[3px] min-h-0">
                    {zones.side.map(id => <div key={id} className={`flex-1 rounded-[1px] ${rest}`} />)}
                </div>
            </div>
            {zones.bottom.map(id => <div key={id} className={`h-[3px] rounded-[1px] shrink-0 ${rest}`} />)}
        </div>
    );
};

interface PresetPickerProps {
    layout: DashboardLayout;
    onChoose: (id: PresetId) => void;
    /** Handy: eine Karte je Zeile. */
    stacked?: boolean;
}

/** Die drei Standard-Dashboards; die passende Vorlage ist markiert, sonst keine. */
export const PresetPicker: React.FC<PresetPickerProps> = ({ layout, onChoose, stacked = false }) => {
    const active = matchingPreset(layout);
    return (
        <div role="group" aria-label="Vorlagen" className={`grid gap-2 ${stacked ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-3'}`}>
            {PRESETS.map(p => {
                const on = active === p.id;
                return (
                    <button
                        key={p.id}
                        type="button"
                        aria-pressed={on}
                        onClick={() => onChoose(p.id)}
                        className={`text-left flex items-start gap-3 rounded-xl border p-3 min-h-[44px] transition-colors ${on ? 'border-blue-600 bg-blue-50/40' : 'border-slate-200 bg-white hover:border-slate-300 active:bg-slate-50'}`}
                    >
                        <PresetSchematic zones={p.zones} active={on} />
                        <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5">
                                <span className={`text-[12px] font-semibold ${on ? 'text-blue-800' : 'text-slate-800'}`}>{p.title}</span>
                                {on && <Check className="w-3.5 h-3.5 text-blue-700 shrink-0" aria-hidden="true" />}
                            </span>
                            <span className="block text-[11px] text-slate-500 leading-snug mt-0.5">{p.description}</span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

interface KpiChooserProps {
    layout: DashboardLayout;
    onToggle: (id: KpiId) => void;
    /** Handy: 44 px hohe Chips. */
    large?: boolean;
}

/** Kacheln der Leiste „Heute im Blick" an- und abwaehlen; die letzte bleibt. */
export const KpiChooser: React.FC<KpiChooserProps> = ({ layout, onToggle, large = false }) => {
    const onlyOne = layout.kpis.length === 1;
    return (
        <div>
            <div role="group" aria-label="Kacheln „Heute im Blick“" className="flex flex-wrap gap-1.5">
                {ALL_KPIS.map(k => {
                    const on = layout.kpis.includes(k);
                    const locked = on && onlyOne;
                    return (
                        <button
                            key={k}
                            type="button"
                            role="switch"
                            aria-checked={on}
                            aria-disabled={locked || undefined}
                            title={locked ? 'Mindestens eine Kachel bleibt sichtbar' : undefined}
                            onClick={() => { if (!locked) onToggle(k); }}
                            className={`inline-flex items-center gap-1 rounded-lg border px-2.5 text-[11px] font-semibold transition-colors ${large ? 'min-h-[44px]' : 'h-8'} ${on ? 'border-blue-200 bg-blue-50 text-blue-800' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700'} ${locked ? 'cursor-default' : ''}`}
                        >
                            {on && <Check className="w-3 h-3 shrink-0" aria-hidden="true" />}
                            {KPI_LABEL[k]}
                        </button>
                    );
                })}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
                {onlyOne ? 'Mindestens eine Kachel bleibt sichtbar.' : `${layout.kpis.length} von ${ALL_KPIS.length} Kacheln sichtbar.`}
            </p>
        </div>
    );
};

interface SaveStatusProps {
    state: LayoutSaveState;
    onRetry: () => void;
    className?: string;
}

/** Speichert… / Gespeichert / Nicht gespeichert (mit erneutem Versuch). */
export const SaveStatus: React.FC<SaveStatusProps> = ({ state, onRetry, className = '' }) => {
    if (state === 'error') {
        return (
            <button
                type="button"
                onClick={onRetry}
                className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-red-200 bg-red-50 text-[11px] font-semibold text-red-700 hover:bg-red-100 ${className}`}
            >
                <RefreshCw className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                Nicht gespeichert – erneut versuchen
            </button>
        );
    }
    return (
        <span role="status" aria-live="polite" className={`inline-flex items-center gap-1.5 text-[11px] ${state === 'saved' ? 'text-emerald-700 font-semibold' : 'text-slate-500'} ${className}`}>
            {state === 'saving' && <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" aria-hidden="true" />}
            {state === 'saved' && <CheckCircle2 className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
            {state === 'saving' ? 'Speichert…' : state === 'saved' ? 'Gespeichert' : 'Änderungen werden für dich gespeichert'}
        </span>
    );
};
